/**
 * Per-tick RESTART STAGE LOCK — who is being moved by the set-piece walk-in
 * this tick, and therefore must not also be moved by his own controller. (§5.1)
 *
 * ── The defect this exists to fix ───────────────────────────────────────────
 *
 * Owner report, verbatim: *"when corner or throw in happens bots are getting
 * dragged they are not running / please make sure each and every player runs or
 * walks or sprint / no forced dragging"*.
 *
 * rules/fsm.ts `applyStageTargets` steps each staged player a capped distance
 * toward his mark by writing px/pz directly. It used to ALSO zero his velocity,
 * for a defensible-sounding reason: step.ts stage 4 integrates velocity into
 * position, so leaving velocity behind would move him a second time in the same
 * tick.
 *
 * But velocity is not private bookkeeping — it is the sim's public statement of
 * what a player is DOING, and half the engine reads it:
 *
 *   - player-controller.ts `stepAnim`/`locomotionAnim` pick the locomotion clip
 *     from |v| alone. v = 0 ⇒ AnimState.Idle. So a player whose position was
 *     being stepped 0.1 m every tick was rendered STANDING STILL while he
 *     translated across the grass: dragged.
 *   - `stepStamina` bands effort by |v|, so a 60 m walk-in cost nothing.
 *   - ai/** reads teammate/opponent velocity to lead passes and judge closing
 *     speed; every staged player looked stationary to it.
 *
 * ── The fix: gate the mover, do not falsify the state ───────────────────────
 *
 * `applyStageTargets` now writes the velocity that MATCHES the step it applied
 * (direction × the pace actually used this tick), so |v| = |Δp| / FIXED_DT is
 * true by construction and every consumer above reads a genuine walk/jog/run.
 * The double-move is prevented instead by LOCKING the staged player out of his
 * own locomotion for that tick, which is what this module carries.
 *
 * ── Why a module-scope buffer and not a WorldState field ────────────────────
 *
 * It is a per-tick CONTROL HINT that flows one way between two stages of the
 * SAME tick — written by step.ts stage 2 (the FSM), read by stage 4 (the player
 * controller) — and never read across ticks. Exactly the argument step.ts's
 * `humanMask` parameter already makes for itself: it is not part of the
 * authoritative state, so it is never hashed, cloned or serialized and it cannot
 * desync (§15.3, D4). Putting it in `PlayerArrays` would mean a hashed field, a
 * clone entry and a wire decision for information that is derivable and dead by
 * the end of the tick.
 *
 * ── Why the tick stamp is load-bearing ─────────────────────────────────────
 *
 * Module scope is shared by every WorldState in the process — the client runs a
 * predicted world alongside re-simulations, and the tests build many worlds. Two
 * guards make that safe:
 *
 *   1. `beginStageLockTick` is called UNCONDITIONALLY at the top of
 *      `stepMatchStateMachine`, every tick and in every phase. So a world that is
 *      not staging anybody always clears the buffer before anything can read it,
 *      and one world's corner can never lock another world's players.
 *   2. `isStageLocked` only believes the buffer if it was stamped with the tick
 *      being asked about. A controller called without the FSM having run (a
 *      direct unit test of stepPlayerController) therefore sees no locks at all
 *      and behaves exactly as it did before this existed.
 */

import type { WorldState } from '../types.js';
import { MAX_PLAYERS } from '../types.js';

/** 1 ⇒ this slot's position/velocity/facing were authored by the walk-in this tick. */
const locked = new Uint8Array(MAX_PLAYERS);

/**
 * The tick `locked` describes. -1 = "nothing has stamped it", which no real tick
 * can equal (step.ts increments from 0), so a stale buffer is never believed.
 */
let lockedTick = -1;

/**
 * Drop last tick's locks and stamp the buffer for THIS tick. Called once per
 * tick, unconditionally, from the top of `stepMatchStateMachine` — see the
 * header for why "unconditionally" is the part that matters.
 */
export function beginStageLockTick(state: WorldState): void {
  locked.fill(0);
  lockedTick = state.tick;
}

/** The walk-in owns player `i`'s locomotion this tick. */
export function setStageLock(i: number): void {
  locked[i] = 1;
}

/**
 * Hand player `i` his own locomotion back. Used for HUMAN-controlled slots,
 * whose authoritative position `runRestart` restores after staging has run: a
 * human is never staged, so he must never be locked either.
 */
export function clearStageLock(i: number): void {
  locked[i] = 0;
}

/**
 * Is player `i`'s locomotion owned by the restart walk-in on `state.tick`?
 *
 * False whenever the FSM has not stamped this exact tick, so this is fail-safe:
 * the worst a missing stamp can do is give a staged player his ordinary
 * locomotion back for a tick, never freeze a player who should be moving.
 */
export function isStageLocked(state: WorldState, i: number): boolean {
  return lockedTick === state.tick && locked[i] === 1;
}
